import { useCallback, useRef, useEffect } from 'react';
import { log } from 'log';
import { pick } from 'ramda';
import { useSocketSelector } from 'js/store/hooks';
import { type SocketIOClientSocket } from 'socket.io-client';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';

import * as helpers from './helpers';
import * as constants from './constants';
import { BATCH_REQUEST_ID } from '../../constants';
import { addStackEntry, createNoticeTimer, createTimeoutTimer, createAbortHandler } from '../../helpers';
import { IServerSendResponse, TServerSendRequest, IDeferredStackEntry, ISocketStackEntry } from '../../types';

export const useGenericSend = () => {
    const {
        connection,
        connected,
        isError
    } = useSocketSelector(pick(['connection', 'connected', 'isError']));
    const deferredStack = useRef<IDeferredStackEntry[]>([]);
    const socketStateRef = useRef<{
        connection?: SocketIOClientSocket;
        connected: boolean;
        isError: boolean;
    } | null>(null);

    const send = useCallback(<RequestData, ResponseData>(
        request: TServerSendRequest<RequestData>,
        options?: { signal?: AbortSignal }
    ) => {
        return new Promise<IServerSendResponse<ResponseData>>((resolve, reject) => {
            const timestamp = Date.now();

            if (socketStateRef.current?.isError || !socketStateRef.current?.connection) {
                reject({
                    reason: 'No connection',
                    request,
                    response: {
                        ID: -1,
                        data: {
                            customError: 'playtech.mobile.system.oapi.error.noConnection'
                        }
                    }
                });

                return;
            }

            if (options?.signal?.aborted) {
                reject({
                    reason: 'Request aborted',
                    request,
                    response: {
                        ID: -1,
                        data: {
                            customError: 'playtech.mobile.system.systemError'
                        }
                    }
                });

                return;
            }

            const updatedRequest = helpers.updateRequest(request);

            if (request.ID === BATCH_REQUEST_ID && updatedRequest.requests) {
                const promises: Promise<void>[] = [];

                for (const subRequest of updatedRequest.requests) {
                    const promiseWithResolvers = Promise.withResolvers<void>();

                    promises.push(promiseWithResolvers.promise);

                    const stackEntry: ISocketStackEntry = {
                        request: subRequest,
                        resolve: promiseWithResolvers.resolve,
                        reject: promiseWithResolvers.reject,
                        timestamp,
                        timer: createTimeoutTimer(() => stackEntry, reject, subRequest, constants.REQUEST_TIMEOUT),
                        noticeTimer: createNoticeTimer(request, constants.REQUEST_NOTICE_TIMEOUT)
                    };

                    options?.signal && createAbortHandler(stackEntry, options.signal, subRequest, reject);
                    addStackEntry(stackEntry);
                }

                const stackEntry: ISocketStackEntry = {
                    request: updatedRequest,
                    resolve,
                    reject,
                    timestamp,
                    timer: createTimeoutTimer(() => stackEntry, reject, updatedRequest, constants.REQUEST_TIMEOUT),
                    noticeTimer: createNoticeTimer(request, constants.REQUEST_NOTICE_TIMEOUT)
                };

                options?.signal && createAbortHandler(stackEntry, options.signal, updatedRequest, reject);
                addStackEntry(stackEntry);
                socketStateRef.current?.connection.send(JSON.stringify(updatedRequest));

                return Promise.all(promises)
                    .catch(reject);
            }

            if (request.responses) {
                const stackEntry: ISocketStackEntry = {
                    request: updatedRequest,
                    resolve,
                    reject,
                    timestamp,
                    timer: createTimeoutTimer(() => stackEntry, reject, updatedRequest, constants.REQUEST_TIMEOUT),
                    noticeTimer: createNoticeTimer(request, constants.REQUEST_NOTICE_TIMEOUT)
                };

                options?.signal && createAbortHandler(stackEntry, options.signal, updatedRequest, reject);
                addStackEntry(stackEntry);
            }

            socketStateRef.current?.connection.send(JSON.stringify(updatedRequest));
        }).catch((error) => {
            publicEventsAPIService
                .trigger('socket-breakdown', {
                    reason: error.reason,
                    requestId: error.request.ID
                });

            log('oapiError', error);

            log('oapiInfo', [
                'The above error occurred because OpenAPI replied with the error.',
                'Please expand all objects within the error response above (both request and response).',
                'Share it with OpenAPI team to retrieve the original server log for the error.'
            ].join('\n'));

            throw error;
        });
    }, []);

    useEffect(() => {
        socketStateRef.current = {
            connection,
            connected,
            isError
        };
    }, [connection, connected, isError]);

    useEffect(() => {
        if ((connected || isError) && deferredStack.current.length) {
            for (const deferredEntry of deferredStack.current) {
                const {
                    request,
                    resolve,
                    reject,
                    options
                } = deferredEntry;

                send(request, options)
                    .then(resolve)
                    .catch(reject);

                deferredStack.current = deferredStack.current.filter(entry => entry !== deferredEntry);
            }
        }
    }, [send, connected, isError]);

    return useCallback(
        <RequestData = object, ResponseData = object>(
            request: TServerSendRequest<RequestData>,
            options?: { signal?: AbortSignal }
        ): Promise<IServerSendResponse<ResponseData>> => {
            log('oapiRequest', request);

            if (socketStateRef.current?.isError || socketStateRef.current?.connected) {
                return send(request, options);
            }

            // fulfill the promise when the connection be established or failed
            return new Promise((resolve, reject) => {
                deferredStack.current.push({
                    request,
                    resolve,
                    reject,
                    options
                });
            });
        },
        [send]
    );
};
